import React, { RefObject, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@saucelabs/ds-modal';
import { InputText } from '@saucelabs/ds-inputs';
import { modalConnectV2 } from '@saucelabs/sl-container-modal';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import Leaflet, { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import constants from '../constants';
import selectors from '../selectors';
import actions from '../actions';
import operations from '../operations';

// ugly workaround for https://github.com/PaulLeCam/react-leaflet/issues/255
Leaflet.Icon.Default.imagePath = '//cdnjs.cloudflare.com/ajax/libs/leaflet/1.2.0/images/';

const UpdateMap = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    latitude,
    longitude,
    zoom,
  } = useSelector(selectors.gpsLocationSelector);

  const position: LatLngTuple = [latitude, longitude];
  const map = useMap();
  const mapEvents = useMapEvents({
    zoomend: () => {
      dispatch(actions.changeGPSLocation({
        zoom: mapEvents.getZoom(),
      }));
    },
  });

  map.setView(position, zoom);
  return null;
};

const GPSLocationModalComponent: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    latitude,
    longitude,
    zoom,
  } = useSelector(selectors.gpsLocationSelector);
  const [latitudeInput, setLatitudeInput] = useState(latitude.toString());
  const [longitudeInput, setLongitudeInput] = useState(longitude.toString());
  const markerRef = useRef<Leaflet.Marker>(null);
  const position: LatLngTuple = [latitude, longitude];

  const markerEventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          const newPosition = marker.getLatLng();
          setLatitudeInput(newPosition.lat.toString());
          setLongitudeInput(newPosition.lng.toString());
          dispatch(actions.changeGPSLocation({
            latitude: newPosition.lat,
            longitude: newPosition.lng,
          }));
        }
      },
    }),
    [dispatch],
  );

  const confirmGPSLocation = () => {
    void dispatch(operations.submitGPSLocation({ latitude, longitude }));
  };

  return (
    <Modal
      onConfirm={confirmGPSLocation}
      size="large"
      title="Set GPS"
    >
      <div>
        <div className="flex mb-1">
          <InputText
            className="w-1/2 pr-1"
            data-qa="gps-location-modal-input-latitude"
            inputMode="decimal"
            isReadOnly={false}
            label="Latitude"
            onChange={(value) => {
              setLatitudeInput(value);
              void dispatch(actions.changeGPSLocation({
                latitude: parseFloat(value) || 0.0,
              }));
            }}
            value={latitudeInput}
          />
          <InputText
            className="w-1/2 pl-1"
            data-qa="gps-location-modal-input-longitude"
            inputMode="decimal"
            label="Longitude"
            onChange={(value) => {
              setLongitudeInput(value);
              void dispatch(actions.changeGPSLocation({
                longitude: parseFloat(value) || 0.0,
              }));
            }}
            value={longitudeInput}
          />
        </div>

        <MapContainer
          center={position}
          style={{ height: 400, width: '100%', overflow: 'hidden' }}
          zoom={zoom}
        >
          <TileLayer
            attribution='&copy; <a href=\"http://osm.org/copyright\">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <Marker
            ref={markerRef}
            draggable
            eventHandlers={markerEventHandlers}
            position={position}
          />
          <UpdateMap />
        </MapContainer>
      </div>
    </Modal>
  );
};

const GPSLocationModal = modalConnectV2(
  constants.GPS_LOCATION_MODAL_IDENTIFIER,
  GPSLocationModalComponent,
);

export default GPSLocationModal;
export {
  GPSLocationModalComponent,
};
